/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { ProductDetail, ProductNote, ProductOrders } from "../../../../shared/services/interface";
import InputBox from "../../components/InputBox";
import { notifyError, notifyWarning } from "../../../../assets/toastService";
import BarcodeScanner from "../../../../shared/utils/scanningBarcode/BarcodeScanner";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import DropdownSelect from "../../components/DropdownSelect";
import { enableStock } from "./ProductDisplay";
import { useUser } from "../../components/UserContext";
import NumberPad from "./NumberPad";

function NotesProducts({ product, orderProduct, lastProduct, updateProduct, viewProduct, closeProduct, clearData, getUOM }: ProductOrders) {
  const [productDetails, setProductDetails] = useState<ProductDetail[]>([
    {
      productId: "",
      productName: "",
      qty: "",
      price: "",
      arabicword: "",
      amount: 0,
      product_id: "",
      uom: "",
      typesUOM: [],
      stock: 0,
      notes: [],
      visibleNotes: false,
    },
  ]);
  const [fullView, setFullview] = useState<boolean>(false);
  const [productSuggestions, setProductSuggestions] = useState<[string, string][][]>([[]]);
  const [findId, setFindId] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const nameRefs = useRef<(HTMLInputElement | null)[]>([]);
  const qtyRefs = useRef<(HTMLInputElement | null)[]>([]);
  const product_id = useRef<(HTMLInputElement | null)[]>([]);
  const { userConfigration } = useUser();
  const [noteText, setNoteText] = useState<string[]>([]);
  const [noteAmount, setNoteAmount] = useState<number[]>([]);
  const noteTextRefs = useRef<(HTMLInputElement | null)[]>([]);
  const noteAmountRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showPad, setShowPad] = useState(false);
  const [activeField, setActiveField] = useState<"qty" | "price" | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const [editingNote, setEditingNote] = useState<{
    productIndex: number;
    noteIndex: number;
  } | null>(null);

  const MAX_NOTES = 8;

  const handleAddNote = (index: number, note: ProductNote) => {
    const updated = [...productDetails];
    const currentNotes: ProductNote[] = updated[index].notes || [];

    // EDIT MODE
    if (editingNote && editingNote.productIndex === index) {
      currentNotes[editingNote.noteIndex] = note;
      updated[index].notes = [...currentNotes];
      setProductDetails(updated);
      setEditingNote(null);
      return;
    }

    // ❌ Block if already 8 notes
    if (currentNotes.length >= MAX_NOTES) {
      notifyWarning("Maximum 8 notes allowed per product");
      return;
    }

    updated[index].notes = [...currentNotes, note];
    setProductDetails(updated);
  };

  const handleDeleteNote = (productIndex: number, noteIndex: number) => {
    const updated = [...productDetails];
    const notes = [...(updated[productIndex].notes || [])];

    notes.splice(noteIndex, 1);
    updated[productIndex].notes = notes;

    setProductDetails(updated);
  };

  const handleEditNote = (productIndex: number, noteIndex: number) => {
    const note = productDetails[productIndex].notes?.[noteIndex];
    if (!note) return;

    const textUpdated = [...noteText];
    textUpdated[productIndex] = note.text;
    setNoteText(textUpdated);

    const amountUpdated = [...noteAmount];
    amountUpdated[productIndex] = note.amount;
    setNoteAmount(amountUpdated);

    setEditingNote({
      productIndex,
      noteIndex,
    });

    setTimeout(() => {
      noteTextRefs.current[productIndex]?.focus();
    }, 100);
  };

  useEffect(() => {
    setNoteText((prev) => [...prev, ""]);
    setNoteAmount((prev) => [...prev, 0]);
  }, [productDetails.length]);

  useEffect(() => {
    if (userConfigration?.isDefaultTabsEnabled) {
      setFullview(Boolean(userConfigration.isDefaultTabsEnabled));
    }
  }, []);

  const handleChange = (index: number, key: keyof ProductDetail, value: string, id?: string) => {
    const numericFields: (keyof ProductDetail)[] = ["qty", "price"];

    if (key === "qty") {
      const UOM = getUOM.find((x) => x.id === productDetails[index].uom);

      if (value === "" || Number(value) === 0) {
        handleDeleteProduct(index);
        return;
      }

      if (UOM && enableStock) {
        const stockConvation = productDetails[index].stock / Number(UOM.conversion_factor);
        const qty = Number(value);

        if (qty > stockConvation) {
          notifyWarning("Quantity exceeds available stock");
          return;
        }
      }
    }

    if (numericFields.includes(key)) {
      if (value === "") {
        const updated = [...productDetails];
        updated[index] = { ...updated[index], [key]: "" };
        setProductDetails(updated);
        return;
      }

      const positiveNumberRegex = /^(0|[1-9]\d*)(\.\d{0,2})?$/;

      if (!positiveNumberRegex.test(value)) return;

      const updated = [...productDetails];
      updated[index] = { ...updated[index], [key]: value };
      setProductDetails(updated);
      return;
    }

    const updated = [...productDetails];

    /* ⭐ OTHER PRODUCT SUPPORT */
    if (key === "productId" && id === "other") {
      updated[index] = {
        ...updated[index],
        productId: "other",
        productName: "Other",
        product_id: "other",
        qty: "1",
        price: "0",
        arabicword: "آحرون",
        uom: "",
        typesUOM: [],
        amount: 0,
        stock: 0,
        notes: [],
        visibleNotes: false,
      };

      const isLastRow = index === productDetails.length - 1;

      if (isLastRow) {
        updated.push({
          productId: "",
          productName: "",
          qty: "",
          price: "",
          arabicword: "",
          product_id: "",
          uom: "",
          typesUOM: [],
          amount: 0,
          stock: 0,
        });
      }
      if (productDetails.filter((p) => p.productId === "other").length < 1) {
        setActiveIndex(index);
        setActiveField("price");
        setShowPad(true);
      }

      setProductDetails((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [key]: value };
        return updated;
      });

      const suggestions = [...productSuggestions];
      suggestions[index] = [["Other", "other"]];
      setProductSuggestions(suggestions);

      return;
    }

    updated[index] = { ...updated[index], [key]: value };
    setProductDetails(updated);

    if (key === "productId") {
      const matches = product.filter((p) => p._id.$oid === id).map((p) => [p.ProductName, p._id.$oid] as [string, string]);

      const suggestions = [...productSuggestions];
      suggestions[index] = matches;
      setProductSuggestions(suggestions);
    }

    if (key === "uom") {
      const updated = [...productDetails];

      if (!value) {
        updated[index] = {
          ...updated[index],
          uom: "",
          price: String(updated[index].amount),
        };
        setProductDetails(updated);
        return;
      }

      const selectedUOM = getUOM.find((u) => u.id === value);

      const basePrice = Number(updated[index].amount) || 0;
      const updatedPrice = basePrice * Number(selectedUOM?.conversion_factor || 1);

      const prosuctQty = Number(updated[index].qty);
      const stockConvation = updated[index].stock / Number(selectedUOM?.conversion_factor || 1);

      const setQty = enableStock ? (prosuctQty > stockConvation ? stockConvation : prosuctQty) : prosuctQty;

      updated[index] = {
        ...updated[index],
        uom: value,
        price: updatedPrice.toFixed(2),
        qty: setQty.toString(),
      };

      setProductDetails(updated);
    }
  };

  const handleSelectProduct = (index: number, name: string, id: string, product_id: string) => {
    const selected = product.find((p) => p._id.$oid === id);
    const existingIndex = productDetails.findIndex((item, idx) => item.productId === id && idx !== index);

    const updated = [...productDetails];

    if (existingIndex !== -1) {
      const existingQty = Number(updated[existingIndex].qty) || 0;
      updated[existingIndex].qty = (existingQty + 1).toString();

      updated[index] = {
        productId: "",
        productName: "",
        qty: "",
        price: "",
        arabicword: "",
        product_id: "",
        uom: "",
        typesUOM: [],
        amount: 0,
        stock: 0,
      };

      setProductDetails(updated);

      const suggestions = [...productSuggestions];
      suggestions[index] = [];
    } else {
      const findUom = Number(getUOM?.filter((u) => u.id === selected?.amountMeasurement)[0]?.conversion_factor);
      console.log(findUom, Number(selected?.Amount) / Number(findUom));
      updated[index] = {
        ...updated[index],
        productId: id,
        productName: name,
        qty: "1",
        arabicword: String(selected?.ArabicName),
        price: selected?.Amount?.toString() || "",
        product_id: product_id,
        typesUOM: selected?.uom ? selected.uom.map((u) => ({ _id: u._id, CategoryName: u.uomName })) : [],
        uom: selected?.amountMeasurement || "",
        amount: Number(selected?.Amount) / findUom || 0,
        stock: selected?.stock || 0,
      };
      const isLastRow = index === productDetails.length - 1;
      const newProductDetails = [...updated];
      const newSuggestions = [...productSuggestions];
      if (isLastRow) {
        newProductDetails.push({
          productId: "",
          productName: "",
          qty: "",
          price: "",
          arabicword: "",
          product_id: "",
          uom: "",
          typesUOM: [],
          amount: 0,
          stock: 0,
        });
        newSuggestions.push([]);
      }

      setProductDetails(newProductDetails);
      setProductSuggestions(newSuggestions);
    }
  };

  const selectProducts = (inputData: string, index: number) => {
    const matchedProduct = product.find((i) => i.ProductName === inputData || i.barcodeId === inputData);

    const updated = [...productDetails];
    updated[index] = {
      ...updated[index],
      productId: matchedProduct?._id.$oid || "",
      productName: matchedProduct?.ProductName || inputData,
      price: matchedProduct?.Amount?.toString() || "",
      typesUOM: (matchedProduct?.uom || []).map((u) => ({ _id: u._id, CategoryName: u.uomName })),
      uom: "",
    };
    setProductDetails(updated);

    setTimeout(() => qtyRefs.current[index]?.focus(), 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      const inputData = productDetails[index].productName;
      selectProducts(inputData, index);

      if (index === productDetails.length - 1) {
        setProductDetails([
          ...productDetails,
          {
            productId: "",
            productName: "",
            qty: "",
            price: "",
            arabicword: "",
            product_id: "",
            uom: "",
            typesUOM: [],
            amount: 0,
            stock: 0,
          },
        ]);
        setProductSuggestions([...productSuggestions, []]);

        setTimeout(() => {
          nameRefs.current[index + 1]?.focus();
        }, 100);
      }
    }
  };

  const handleSearchProductByBarcode = (ProductId: string) => {
    const index = productDetails.length - 1;
    const filter = product.find((i) => i.barcodeId === ProductId);
    const stock = enableStock ? filter?.stock === 0 : false;
    if (!stock) {
      handleChange(index, "productId", filter?.ProductName || "", filter?._id.$oid);
    } else {
      notifyWarning("Product Stock has null");
    }
  };

  useEffect(() => {
    const index = productDetails.length - 1;
    if (!lastProduct) return;

    // 🟢 Handle OTHER product
    if (lastProduct === "other") {
      handleChange(index, "productId", "Other", "other");
      return;
    }

    // 🟢 Normal DB product
    const filter = product.find((i) => i._id.$oid === lastProduct);

    if (filter) {
      handleChange(index, "productId", filter.ProductName || "", lastProduct);
    }
  }, [orderProduct, lastProduct]);

  useEffect(() => {
    productSuggestions.forEach((suggestions, index) => {
      if (!productDetails[index]) return;
      if (suggestions.length === 1 && productDetails[index].productId !== suggestions[0][1]) {
        const [name, id] = suggestions[0];
        const findId = product.find((i) => i._id.$oid === id)?.product_id;
        handleSelectProduct(index, name, id, findId);
      }
    });
  }, [productSuggestions]);

  useEffect(() => {
    updateProduct(productDetails);
  }, [productDetails]);

  useEffect(() => {
    setFullview(false);
  }, [closeProduct]);

  useEffect(() => {
    setProductDetails([
      { productId: "", productName: "", qty: "", price: "", arabicword: "", product_id: "", uom: "", typesUOM: [], amount: 0, stock: 0 },
    ]);
    setProductSuggestions([[]]);
  }, [clearData]);

  const handleDeleteProduct = (index: number) => {
    const updated = [...productDetails];
    updated.splice(index, 1);

    const updatedSuggestions = [...productSuggestions];
    updatedSuggestions.splice(index, 1);

    if (updated.length === 0) {
      updated.push({
        productId: "",
        productName: "",
        qty: "",
        price: "",
        arabicword: "",
        product_id: "",
        uom: "",
        typesUOM: [],
        amount: 0,
        stock: 0,
      });
      updatedSuggestions.push([]);
    }

    setProductDetails(updated);
    setProductSuggestions(updatedSuggestions);
    setFindId((prev) => [...prev, ""]);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [productDetails.length]);

  const findProduct = (index: number, key: keyof ProductDetail, value: string) => {
    console.log(index);
    const updated = [...findId];
    updated[index] = value;
    setFindId(updated);
  };

  const handleKeyDownFind = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      const idToFind = findId[index];
      const matchedProduct = product.find((p) => p.product_id === idToFind);
      if (matchedProduct) {
        const updated = [...productDetails];
        const existingIndex = productDetails.findIndex((item, i) => item.productId === matchedProduct._id.$oid && i !== index);
        console.log("existingIndex", existingIndex);
        if (existingIndex !== -1) {
          const currentQty = Number(updated[existingIndex].qty || "0");
          updated[existingIndex].qty = (currentQty + 1).toString();

          updated[index] = {
            productId: "",
            productName: "",
            qty: "",
            price: "",
            arabicword: "",
            product_id: "",
            typesUOM: [],
            uom: "N/A",
            amount: 0,
            stock: 0,
          };

          setProductDetails(updated);

          const newFindId = [...findId];
          newFindId[index] = "";
          setFindId(newFindId);

          const emptyRowIndex = updated.findIndex((p) => !p.productId);
          if (emptyRowIndex !== -1) {
            setTimeout(() => nameRefs.current[emptyRowIndex]?.focus(), 100);
          }
        } else {
          updated[index] = {
            productId: matchedProduct._id.$oid,
            productName: matchedProduct.ProductName,
            qty: "1",
            price: matchedProduct.Amount?.toString() || "",
            arabicword: matchedProduct.ArabicName,
            product_id: matchedProduct.product_id,
            typesUOM: (matchedProduct.uom || []).map((u) => ({ _id: u._id, CategoryName: u.uomName })),
            uom: matchedProduct.amountMeasurement,
            amount: 0,
            stock: matchedProduct.stock,
          };

          if (index === productDetails.length - 1) {
            updated.push({
              productId: "",
              productName: "",
              qty: "",
              price: "",
              arabicword: "",
              product_id: "",
              uom: "",
              typesUOM: [],
              amount: 0,
              stock: 0,
            });

            setFindId((prev) => [...prev, ""]);
            setProductSuggestions((prev) => [...prev, []]);
          }

          setProductDetails(updated);

          setTimeout(() => qtyRefs.current[index]?.focus(), 100);
        }
      } else {
        notifyError(`No found with product_id ${idToFind}`);
      }
    }
  };

  const getProductTotal = (item: ProductDetail) => {
    const base = Number(item.price) * Number(item.qty);
    const notesTotal = (item.notes || []).reduce((s, n) => s + Number(n.amount || 0), 0);
    return base + notesTotal;
  };

  const gotonextProductId = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const qty = Number(productDetails[index].qty);

      if (qty === 0) {
        // Remove row if qty is 0
        handleDeleteProduct(index);
        return;
      }
      // const lastData = index.length
      product_id.current[index + 1]?.focus();
    }
  };

  const NotesUpdate = (index: number, item: ProductDetail) => {
    if (!noteText[index]) {
      notifyWarning("Note text required");
      return;
    }

    handleAddNote(index, {
      sNo: `${index + 1}-${(item.notes?.length || 0) + 1}`,
      text: noteText[index],
      amount: noteAmount[index] || 0,
    });

    setNoteText((prev) => {
      const updated = [...prev];
      updated[index] = "";
      return updated;
    });

    setNoteAmount((prev) => {
      const updated = [...prev];
      updated[index] = 0;
      return updated;
    });
  };

  return (
    <div className='w-full border border-b border-gray-100 p-1 shadow-inner rounded-lg h-full overflow-hidden'>
      <div className='w-full h-fit justify-between flex flex-row-reverse px-2'>
        <p
          className='font-light text-blue-600 hover:underline hover:uppercase cursor-pointer'
          onClick={() => {
            setFullview(!fullView);
            viewProduct(fullView);
          }}
        >
          {fullView ? "Close" : "View"} Products Tab
        </p>
        <h1 className='text-xl font-semibold'>Order Details</h1>
      </div>
      <div className={`grid grid-cols-[50px_3fr_50px_1fr_25px] ${ThemeBasedColorHeader()} font-semibold p-2 rounded-t gap-2`}>
        {["#", "Product Details", "Qty", "Total"].map((h, i) => (
          <div key={i} className={`flex items-center justify-${i > 2 ? "end" : "center"}`}>
            {h}
          </div>
        ))}
      </div>
      <div className='h-[86%] overflow-auto border border-gray-200 rounded-md scrollbar-thin scrollbar-thumb-gray-400'>
        {productDetails.map((item, index) => {
          const isValidProduct = !!item.productId;
          return (
            <div
              key={index}
              className={`grid grid-cols-[50px_3fr_50px_1fr_25px] ${ThemeBasedColorMenubar()} font-semibold p-2 rounded-t gap-0.5 my-1`}
              ref={scrollRef}
            >
              <p className='text-center'>{index + 1}</p>
              <div
                className='grid grid-cols-[50px_3fr] items-center justify-center cursor-pointer'
              // onClick={() => {
              //   if (!item.productId) {
              //     notifyWarning("Select product first");
              //     return;
              //   }
              //   setFullview(true);
              // }}
              >
                <InputBox
                  value={item.product_id || findId[index]}
                  onChange={(e) => findProduct(index, "product_id", e.target.value)}
                  className={!isValidProduct ? "border-blue-400 w-20px" : "w-20px"}
                  ref={(el) => (product_id.current[index] = el)}
                  onKeyDown={(e) => {
                    handleKeyDownFind(e, index);
                  }}
                  disabled={!!item.productName}
                />
                <InputBox
                  value={item.productName}
                  onChange={(e) => handleChange(index, "productName", e.target.value)}
                  ref={(el) => (nameRefs.current[index] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  disabled
                />
              </div>
              <div className='hidden'>
                <BarcodeScanner onDetected={(code: string) => handleSearchProductByBarcode(code)} />
              </div>
              <div className='flex'>
                <InputBox
                  type='text'
                  value={item.qty}
                  readOnly
                  disabled={!isValidProduct}
                  ref={(el) => (qtyRefs.current[index] = el)}
                  className='text-right'
                  onClick={() => {
                    if (!isValidProduct) return;
                    setActiveIndex(index);
                    setActiveField("qty");
                    setShowPad(true);
                  }}
                />
              </div>

              <div className='flex justify-end font-semibold'>{getProductTotal(item).toFixed(2)}</div>
              {item.productName && (
                <div id='deleted' className='flex text-red-600 -mt-3 ml-3'>
                  <p onClick={() => handleDeleteProduct(index)} className='cursor-pointer'>
                    ❌
                  </p>
                </div>
              )}
              {item.productId && (
                <div className='col-span-full ml-6 mr-12 mt-0.5 p-1 bg-gray-50 border rounded-lg'>
                  <div className='flex gap-4'>
                    <div
                      onClick={() => {
                        const updated = [...productDetails];
                        updated[index] = { ...updated[index], visibleNotes: !productDetails[index].visibleNotes };
                        setProductDetails(updated);
                      }}
                    >
                      <div className='flex justify-center items-center h-full cursor-pointer'>{item.visibleNotes ? "⏏️" : "🔽"}</div>
                    </div>
                    <div className='grid grid-cols-[1fr_3fr] w-[40%]'>
                      <div className='h-8 w-fit flex gap-2'>
                        <div className='mt-2'>UOM:</div>
                        <div className='w-[120px]'>
                          <DropdownSelect
                            name='uom-select'
                            value={item.uom || "-"}
                            options={item.typesUOM && item.typesUOM.length > 0 ? item.typesUOM : [{ _id: "-", CategoryName: "-" }]}
                            onChange={(e) => handleChange(index, "uom", e)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='grid grid-cols-[2fr_1fr] w-[60%] gap-2'>
                      <div className='flex w-fill justify-end items-center'>Unit Price :</div>
                      <InputBox
                        type='text'
                        value={item.price}
                        readOnly
                        disabled={!isValidProduct}
                        className='text-right'
                        onClick={() => {
                          if (!isValidProduct) return;
                          setActiveIndex(index);
                          setActiveField("price");
                          setShowPad(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {showPad && activeIndex !== null && activeField && (
                <NumberPad
                  value={productDetails[activeIndex][activeField] || ""}
                  onChange={(val) => {
                    handleChange(activeIndex, activeField, val);
                  }}
                  onClose={() => {
                    setShowPad(false);
                    setActiveField(null);
                  }}
                />
              )}

              {item.productId && item.visibleNotes && (
                <div className='col-span-full ml-6 mr-12 p-1 bg-gray-50 border rounded-lg'>
                  {(item.notes ?? []).length > 0 ? (
                    (item.notes || []).map((n, i) => (
                      <div key={n.sNo} className='flex justify-between items-center text-sm text-gray-600 group'>
                        <span className='cursor-pointer hover:text-blue-600' onClick={() => handleEditNote(index, i)}>
                          {i + 1}. {n.text}
                        </span>

                        <div className='flex items-center gap-3'>
                          <span>{Number(n.amount).toFixed(2)}</span>

                          <span className='cursor-pointer text-red-500 hover:text-red-700' onClick={() => handleDeleteNote(index, i)}>
                            ✕
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className='w-full flex justify-center items-center text-gray-400 font-light'>No data avabile</p>
                  )}

                  <hr className='my-2' />

                  <div className='mt-3 grid grid-cols-[3fr_1fr_1fr] gap-2'>
                    {/* Alternation Text */}
                    <InputBox
                      placeholder='Alternation Details'
                      value={noteText[index] || ""}
                      ref={(el) => (noteTextRefs.current[index] = el)}
                      onChange={(e) => {
                        const t = [...noteText];
                        t[index] = e.target.value;
                        setNoteText(t);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          noteAmountRefs.current[index]?.focus();
                        }
                      }}
                    />

                    {/* Amount */}
                    <InputBox
                      type='number'
                      placeholder='Amount'
                      value={noteAmount[index] || ""}
                      ref={(el) => (noteAmountRefs.current[index] = el)}
                      min={0}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value === "") {
                          const updated = [...noteAmount];
                          updated[index] = 0;
                          setNoteAmount(updated);
                          return;
                        }

                        const positiveRegex = /^(0|[1-9]\d*)(\.\d{0,2})?$/;

                        if (!positiveRegex.test(value)) return;

                        const updated = [...noteAmount];
                        updated[index] = Number(value);
                        setNoteAmount(updated);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          NotesUpdate(index, item);
                          noteTextRefs.current[index]?.focus();
                        }
                      }}
                    />

                    {/* Add Button */}
                    <button className='bg-blue-600 text-white rounded-md px-4 py-2' onClick={() => NotesUpdate(index, item)}>
                      +Alt
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
export default NotesProducts;
